import { useMemo } from 'react';

import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { usePageSpaceKey, usePageContentId, useContentType } from '@confluence/page-context';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';

import EndOfPageRecommendationQuery from '../__generated__/EndOfPageRecommendationQuery.graphql.ts';
import {
	type EndOfPageRecommendationQuery as EndOfPageRecommendationData,
	type EndOfPageRecommendationQueryVariables,
	type EndOfPageRecommendationQuery_getRecommendedPages_recommendedPages,
} from '../__types__/EndOfPageRecommendationQuery.ts';
import { transformAndOrderRecs } from '../transformers.ts';

import { useTwgRecommendationData } from './useTwgRecommendationData.ts';

export const usePageRecommendationData = (
	skip: boolean = false,
):
	| {
			loading: boolean;
			error: ApolloErrorInterop | undefined;
			data: undefined;
			hasRecs: boolean;
	  }
	| {
			loading: boolean;
			error: undefined;
			data: GlobalPageCard[] | undefined;
			hasRecs: boolean | undefined;
	  } => {
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();

	// Which arm this user is on. Inlined at each callsite (not stored in a variable)
	// per the feature-gating lint rule so it stays statically analyzable. NO-EXPOSURE
	// reader: this hook runs on view-page load for layout (`hasRecs`), so exposure is
	// logged at the loader instead — the reader is deterministic and fires none, so
	// evaluating it twice is safe.

	// TWG source (skipped in control). Same query/variables as the rendering
	// container, so it dedupes and `hasRecs` matches what's rendered. Passes the real
	// content type so blog posts build a `blogpost/` ARI (not a wrong `page/` one).
	const twg = useTwgRecommendationData({
		entityId: contentId ?? '',
		entityType: contentType ?? 'page',
		spaceKey: spaceKey ?? '',
		skip:
			skip ||
			!UNSAFE_expValEqualsNoExposure('confluence_eop_recs_twg_source_exp', 'isEnabled', true),
	});

	// cc-smarts (control) source. Skipped in the treatment arm.
	const smartsSkip =
		skip || UNSAFE_expValEqualsNoExposure('confluence_eop_recs_twg_source_exp', 'isEnabled', true);
	const baseOptions = {
		variables: {
			type: 'page',
			id: contentId ?? '',
			exp: 'end_of_page_v5',
			key: spaceKey ?? '',
			keyString: spaceKey ?? '',
		},
		context: { single: true },
		errorPolicy: 'all' as const,
		skip: smartsSkip,
	};

	const {
		error: smartsError,
		loading: smartsLoading,
		data: smartsData,
	} = useQueryNoFurtherUpdateInterop<
		EndOfPageRecommendationData,
		EndOfPageRecommendationQueryVariables
	>(EndOfPageRecommendationQuery, baseOptions);

	const smartsPageCardData = useMemo(
		() =>
			transformAndOrderRecs(
				smartsData?.getRecommendedPages
					?.recommendedPages as EndOfPageRecommendationQuery_getRecommendedPages_recommendedPages[],
			),
		[smartsData],
	);

	if (UNSAFE_expValEqualsNoExposure('confluence_eop_recs_twg_source_exp', 'isEnabled', true)) {
		if (twg.error || skip) {
			return {
				loading: false,
				error: twg.error,
				data: undefined,
				hasRecs: false,
			};
		}
		// Gate on `isEnabled`: TWG returns related content regardless of the space/user
		// enablement toggle, but the widget only renders when enabled. Without this, the
		// layout would reserve space for a recs widget that never appears.
		return {
			loading: twg.loading,
			error: undefined,
			data: twg.isEnabled ? twg.pageCardData : undefined,
			// `> 2` intentionally matches the cc-smarts control arm exactly so both arms
			// report `hasRecs` identically — keeps the experiment free of layout-driven
			// confounds. (Pre-existing quirk: the grid renders at >= 2 cards.)
			hasRecs: twg.isEnabled && twg.pageCardData.length > 2,
		};
	}

	if (smartsError || skip) {
		return {
			loading: false,
			error: smartsError,
			data: undefined,
			hasRecs: false,
		};
	}

	return {
		loading: smartsLoading,
		error: smartsError,
		data: smartsPageCardData,
		hasRecs: smartsPageCardData && smartsPageCardData.length > 2,
	};
};
